import { useEffect, useRef, useState } from "react";
import type { OcFlowState, OcPortalConfig } from "../App";
import { OcTurnstileWidget } from "../components/OcTurnstileWidget";
import { ocLookup, ocSendCode } from "../lib/oc-api";

interface OcStep1IdentifierProps {
  config: OcPortalConfig;
  state: OcFlowState;
  setState: (next: OcFlowState) => void;
}

function ocReadSlug(): string {
  if (typeof window === "undefined") return "";
  return window.location.pathname.split("/").filter(Boolean)[0] ?? "";
}

export function OcStep1Identifier({ config, state, setState }: OcStep1IdentifierProps) {
  const [identifier, setIdentifier] = useState<string>(state.identifier ?? "");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState<number>(0);
  const [pending, setPending] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [neutralMessage, setNeutralMessage] = useState<string | null>(null);
  const [rateLockUntil, setRateLockUntil] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!rateLockUntil) {
      setSecondsLeft(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((rateLockUntil - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        setRateLockUntil(null);
      }
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [rateLockUntil]);

  const isLocked = rateLockUntil !== null && secondsLeft > 0;
  const canSubmit =
    identifier.trim().length > 0 && !!turnstileToken && !pending && !isLocked;

  function ocResetTurnstile() {
    setTurnstileToken(null);
    setTurnstileKey((k) => k + 1);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setPending(true);
    setErrorMessage(null);
    setNeutralMessage(null);
    const slug = ocReadSlug();
    try {
      const res = await ocLookup(slug, {
        identifier: identifier.trim(),
        turnstileToken: turnstileToken as string,
      });

      if (!res.error) {
        const { sessionId, allowedChannels } = res;
        if (!sessionId) {
          setNeutralMessage(
            "If a matching account exists, we sent a code to your recovery channel. Check your email or phone."
          );
          ocResetTurnstile();
          setPending(false);
          return;
        }
        if (allowedChannels && allowedChannels.length === 1) {
          const only = allowedChannels[0];
          // Single-channel UX shortcut: auto-send and jump straight to verify.
          const sendRes = await ocSendCode(slug, {
            sessionId,
            channel: only.channel,
          });
          if (!sendRes.error) {
            setState({
              ...state,
              step: "verify",
              identifier: identifier.trim(),
              sessionId,
              allowedChannels,
              selectedChannel: only.channel,
              errorMessage: undefined,
            });
            return;
          }
          // Fall back to the channel screen so the user can retry the send.
          setState({
            ...state,
            step: "channel",
            identifier: identifier.trim(),
            sessionId,
            allowedChannels,
            selectedChannel: undefined,
            errorMessage:
              "We could not send your code automatically. Please choose a channel and try again.",
          });
          return;
        }

        setState({
          ...state,
          step: "channel",
          identifier: identifier.trim(),
          sessionId,
          allowedChannels: allowedChannels ?? [],
          selectedChannel: undefined,
          errorMessage: undefined,
        });
        return;
      }

      if (res.error === "turnstile_failed") {
        setErrorMessage("Verification failed. Please try again.");
        ocResetTurnstile();
        setPending(false);
        return;
      }

      if (res.error === "rate_limited") {
        setErrorMessage("Too many attempts. Please try again in a few minutes.");
        setRateLockUntil(Date.now() + 60_000);
        ocResetTurnstile();
        setPending(false);
        return;
      }

      setErrorMessage("Something went wrong. Please try again.");
      ocResetTurnstile();
      setPending(false);
    } catch {
      setErrorMessage("We could not reach the server. Please check your connection and try again.");
      ocResetTurnstile();
      setPending(false);
    }
  }

  const primary = config.branding?.primaryColor || "#0ea5e9";

  return (
    <div className="oc-step oc-step-identifier">
      <h1 className="text-2xl font-semibold text-slate-900 mb-2">Reset your password</h1>
      <p className="text-sm text-slate-600 mb-6">
        Enter your school email or phone number. If a matching account exists, we will send a verification code to a recovery channel on file.
      </p>

      <form onSubmit={onSubmit} noValidate>
        <label htmlFor="oc-identifier" className="block text-sm font-medium text-slate-700 mb-1">
          School email or phone number
        </label>
        <input
          ref={inputRef}
          id="oc-identifier"
          name="identifier"
          type="text"
          autoComplete="username"
          required
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          disabled={pending || isLocked}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-[var(--oc-primary)] focus:ring-2 focus:ring-[var(--oc-primary)]/30 disabled:bg-slate-50 disabled:text-slate-500"
          placeholder="you@school.edu or +1 555 123 4567"
        />

        <div className="mt-4">
          <OcTurnstileWidget
            key={turnstileKey}
            siteKey={config.turnstileSiteKey}
            onToken={(t) => setTurnstileToken(t)}
          />
        </div>

        {errorMessage ? (
          <div
            role="alert"
            className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {errorMessage}
            {isLocked ? ` (${secondsLeft}s)` : null}
          </div>
        ) : null}

        {neutralMessage ? (
          <div
            role="status"
            className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
          >
            {neutralMessage}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit}
          style={{ backgroundColor: primary }}
          className="mt-6 inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
        >
          {pending ? "Working..." : "Continue"}
        </button>
      </form>
    </div>
  );
}

export default OcStep1Identifier;
